import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { usePromotions, followPromoLink } from '../../hooks/usePromotions';

const SESSION_KEY = 'promo-popup-seen';

// Site-wide promotion popup. Shows admin-managed banners once per browser session.
// Multiple promos rotate as a carousel; clicking a banner follows its link.
export default function PromoPopup() {
    const navigate = useNavigate();
    const promos = usePromotions();
    const [open, setOpen] = useState(false);
    const [index, setIndex] = useState(0);
    // Bumped on any MANUAL navigation so the auto-rotate timer restarts — otherwise a
    // click could be immediately overridden by the 5s auto-advance.
    const [manualNonce, setManualNonce] = useState(0);
    const seenRef = useRef(false);

    // Already dismissed this session? Never open again until a new session.
    useEffect(() => {
        try { if (sessionStorage.getItem(SESSION_KEY)) seenRef.current = true; } catch (e) { /* ignore */ }
    }, []);

    // Open once, when promos first become available and not seen this session.
    useEffect(() => {
        if (!seenRef.current && promos.length > 0) setOpen(true);
    }, [promos]);

    // Preload EVERY promo image as soon as the popup opens, so clicking next/prev swaps
    // instantly instead of downloading a few-hundred-KB image on each click (which made
    // the banner look "stuck" on a fresh/incognito visit).
    useEffect(() => {
        if (!open) return;
        promos.forEach(p => { if (p.imageUrl) { const im = new Image(); im.src = p.imageUrl; } });
    }, [open, promos]);

    // Auto-rotate the carousel; restarts whenever the user navigates manually.
    useEffect(() => {
        if (!open || promos.length <= 1) return;
        const t = setInterval(() => setIndex(i => (i + 1) % promos.length), 5000);
        return () => clearInterval(t);
    }, [open, promos.length, manualNonce]);

    const close = () => {
        setOpen(false);
        seenRef.current = true;
        try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) { /* ignore */ }
    };

    const goBy = (dir) => { setIndex(i => (i + dir + promos.length) % promos.length); setManualNonce(n => n + 1); };
    const goTo = (i) => { setIndex(i); setManualNonce(n => n + 1); };
    const handleClick = (promo) => { followPromoLink(promo.linkUrl, navigate); close(); };

    if (!open || promos.length === 0) return null;
    const safeIndex = Math.min(index, promos.length - 1);
    const promo = promos[safeIndex];

    return createPortal(
        <div
            className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
            onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
            <div className="relative w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
                {/* Close */}
                <button
                    onClick={close}
                    aria-label="ปิด"
                    className="absolute -top-3 -right-3 z-30 w-9 h-9 rounded-full bg-white text-gray-800 shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
                >
                    ✕
                </button>

                {/* Banner (clickable → link) */}
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                    <img
                        src={promo.imageUrl}
                        alt={promo.title || 'promotion'}
                        onClick={() => handleClick(promo)}
                        className="w-full max-h-[78vh] object-contain bg-black cursor-pointer"
                    />
                </div>

                {/* Carousel controls (only when multiple) */}
                {promos.length > 1 && (
                    <>
                        <button onClick={(e) => { e.stopPropagation(); goBy(-1); }} aria-label="ก่อนหน้า"
                            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/55 hover:bg-black/75 text-white text-xl flex items-center justify-center transition-colors">‹</button>
                        <button onClick={(e) => { e.stopPropagation(); goBy(1); }} aria-label="ถัดไป"
                            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/55 hover:bg-black/75 text-white text-xl flex items-center justify-center transition-colors">›</button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
                            {promos.map((_, i) => (
                                <button key={i} onClick={(e) => { e.stopPropagation(); goTo(i); }} aria-label={`สไลด์ ${i + 1}`}
                                    className={`h-2 rounded-full transition-all ${i === safeIndex ? 'bg-white w-5' : 'bg-white/50 w-2'}`} />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
}
